import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, UntypedFormControl, Validators } from '@angular/forms';
import { FileInputComponent } from '@catch/file-input';
import {
  ButtonComponent,
  ModalComponent,
  RadioCardComponent,
  RadioCardGroupComponent,
  TagComponent,
} from '@catch/general-ui';
import { faFileUpload, faUser } from '@fortawesome/pro-light-svg-icons';
import { TranslocoPipe } from '@jsverse/transloco';
import { forkJoin, switchMap } from 'rxjs';
import { PlatformApplicationImportSource } from '../../enums/platform-application-import-source.enum';
import { PlatformOnboardingPath } from '../../enums/platform-onboarding-path.enum';
import { PlatformDocument, PlatformImportableApplication } from '../../models/platform.models';
import { PlatformAuthService } from '../../services/platform-auth.service';
import { PlatformCandidateApiService } from '../../services/platform-candidate-api.service';
import { PlatformJobsService } from '../../services/platform-jobs.service';
import { PlatformProfileService } from '../../services/platform-profile.service';

@Component({
  selector: 'app-platform-onboarding-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ButtonComponent,
    FileInputComponent,
    ModalComponent,
    RadioCardComponent,
    RadioCardGroupComponent,
    TagComponent,
  ],
  templateUrl: './platform-onboarding-modal.component.html',
  styleUrl: './platform-onboarding-modal.component.scss',
})
export class PlatformOnboardingModalComponent implements AfterViewInit {
  private readonly auth = inject(PlatformAuthService);
  private readonly profile = inject(PlatformProfileService);
  private readonly candidateApi = inject(PlatformCandidateApiService);
  private readonly jobs = inject(PlatformJobsService);
  private readonly fb = inject(FormBuilder);
  private readonly modal = viewChild<ModalComponent>('onboardingModal');

  readonly icons = {
    fileArrowUp: faFileUpload,
    user: faUser,
  };

  readonly importSource = PlatformApplicationImportSource;
  readonly onboardingPath = PlatformOnboardingPath;
  readonly importableApplications = signal<PlatformImportableApplication[]>([]);
  readonly selectedApplicationIds = signal<number[]>([]);
  readonly unknownStep = signal<1 | 2>(1);
  readonly extracting = signal(false);
  readonly cvControl = new UntypedFormControl(null);
  readonly hasCvFile = signal(false);
  readonly profileForm: FormGroup = this.fb.group({
    firstName: this.fb.control('', Validators.required),
    lastName: this.fb.control('', Validators.required),
    phone: this.fb.control(''),
  });
  readonly skills = signal<string[]>([]);
  readonly profileSubmitAttempt = signal(false);
  readonly importResolved = signal(false);
  private readonly requestedOpen = signal(false);

  readonly path = computed(() => {
    if (this.auth.onboardingPath()) {
      return this.auth.onboardingPath();
    }
    if (!this.importResolved()) {
      return null;
    }
    return this.importableApplications().length > 0
      ? PlatformOnboardingPath.KNOWN
      : PlatformOnboardingPath.UNKNOWN;
  });

  readonly sessionEmail = computed(() => this.auth.session()?.email ?? '');
  readonly existingDocuments = computed<PlatformDocument[]>(() => this.profile.profile().documents);
  readonly hasExistingProfile = computed(() => {
    const profile = this.profile.profile();
    return (
      profile.documents.length > 0 ||
      profile.skills.length > 0 ||
      Boolean(profile.phone) ||
      Boolean(profile.firstName && profile.lastName)
    );
  });

  constructor() {
    this.cvControl.valueChanges.subscribe((value) => {
      this.hasCvFile.set(value instanceof File);
    });
    effect(() => {
      const shouldOpen = this.auth.loggedIn() && !this.auth.onboardingCompleted();
      untracked(() => {
        if (shouldOpen) {
          this.prepareAndOpen();
          return;
        }
        this.resetAndClose();
      });
    });
  }

  /**
   * Opens the modal once the host is in the view.
   */
  ngAfterViewInit(): void {
    if (this.requestedOpen() && this.path()) {
      this.modal()?.open();
    }
  }

  /**
   * Updates the selected applications to import.
   * @param value - Radio card group value
   */
  onImportSelectionChange(value: unknown): void {
    this.selectedApplicationIds.set(
      Array.isArray(value) ? value.filter((item): item is number => typeof item === 'number') : []
    );
  }

  /**
   * Imports selected applications and closes the assistant.
   */
  completeKnownOnboarding(): void {
    this.candidateApi.importApplications(this.selectedApplicationIds()).subscribe({
      next: () => this.finishOnboarding(),
    });
  }

  /**
   * Skips application import and closes the assistant.
   */
  skipKnownOnboarding(): void {
    this.candidateApi.importApplications([]).subscribe({
      next: () => this.finishOnboarding(),
    });
  }

  /**
   * Continues with an optional new CV or with data already on the profile.
   */
  continueUnknownStep(): void {
    const file = this.cvControl.value as File | null;
    if (file) {
      this.parseCvAndConfirm(file);
      return;
    }
    this.skipCvUpload();
  }

  /**
   * Continues without parsing a new CV.
   */
  skipCvUpload(): void {
    this.prefillFromExistingProfile();
    this.unknownStep.set(2);
  }

  /**
   * Returns from the confirm step to the optional CV step.
   */
  backToUnknownStart(): void {
    this.unknownStep.set(1);
  }

  /**
   * Removes an extracted skill.
   * @param skill - Skill label to remove
   */
  removeSkill(skill: string): void {
    this.skills.update((skills) => skills.filter((item) => item !== skill));
  }

  /**
   * Saves the confirmed profile and closes the assistant.
   */
  completeUnknownOnboarding(): void {
    this.profileSubmitAttempt.set(true);
    if (this.profileForm.invalid) {
      return;
    }
    this.candidateApi
      .updateProfile({
        firstName: this.profileForm.value.firstName,
        lastName: this.profileForm.value.lastName,
        phone: this.profileForm.value.phone || null,
        skills: this.profile.skillsFromNames(this.skills()),
      })
      .pipe(switchMap(() => this.auth.completeOnboarding()))
      .subscribe({
        next: () => {
          this.jobs.reloadAll();
          this.modal()?.close();
        },
      });
  }

  get firstName() {
    return this.profileForm.get('firstName')!;
  }

  get lastName() {
    return this.profileForm.get('lastName')!;
  }

  /**
   * Loads importable applications and opens the assistant when needed.
   */
  private prepareAndOpen(): void {
    if (this.requestedOpen()) {
      if (this.importResolved()) {
        this.modal()?.open();
      }
      return;
    }
    this.requestedOpen.set(true);
    forkJoin({
      importable: this.candidateApi.importableApplications(),
      applications: this.candidateApi.applications(),
    }).subscribe({
      next: ({ importable, applications }) => {
        if (importable.length === 0 && applications.length > 0) {
          this.finishOnboarding();
          return;
        }
        if (importable.length > 0) {
          this.importableApplications.set(importable);
          this.selectedApplicationIds.set(importable.map((application) => application.id));
          this.auth.setOnboardingPath(PlatformOnboardingPath.KNOWN);
          this.importResolved.set(true);
          this.modal()?.open();
          return;
        }
        this.prefillFromExistingProfile();
        this.unknownStep.set(this.hasExistingProfile() ? 2 : 1);
        this.auth.setOnboardingPath(PlatformOnboardingPath.UNKNOWN);
        this.importResolved.set(true);
        this.modal()?.open();
      },
      error: () => {
        this.prefillFromExistingProfile();
        this.unknownStep.set(this.hasExistingProfile() ? 2 : 1);
        this.auth.setOnboardingPath(PlatformOnboardingPath.UNKNOWN);
        this.importResolved.set(true);
        this.modal()?.open();
      },
    });
  }

  /**
   * Parses a CV and continues to the confirmation step.
   * @param file - Uploaded CV
   */
  private parseCvAndConfirm(file: File): void {
    this.extracting.set(true);
    this.profile.parseCv(file).subscribe({
      next: (extracted) => {
        this.profileForm.patchValue({
          firstName: extracted.firstName || this.profileForm.value.firstName || '',
          lastName: extracted.lastName || this.profileForm.value.lastName || '',
          phone: extracted.phone || this.profileForm.value.phone || '',
        });
        if (extracted.skills?.length) {
          this.skills.set([...extracted.skills]);
        }
        this.extracting.set(false);
        this.unknownStep.set(2);
      },
      error: () => {
        this.extracting.set(false);
        this.unknownStep.set(2);
      },
    });
  }

  /**
   * Fills the confirm step from the existing candidate profile.
   */
  private prefillFromExistingProfile(): void {
    const profile = this.profile.profile();
    const session = this.auth.session();
    this.profileForm.patchValue({
      firstName: profile.firstName || session?.firstName || '',
      lastName: profile.lastName || session?.lastName || '',
      phone: profile.phone || '',
    });
    this.skills.set(profile.skills.map((skill) => skill.name));
  }

  /**
   * Marks onboarding as done and closes the modal.
   */
  private finishOnboarding(): void {
    this.auth.completeOnboarding().subscribe({
      next: () => {
        this.jobs.reloadAll();
        this.modal()?.close();
      },
    });
  }

  /**
   * Resets transient onboarding state after logout or completion.
   */
  private resetAndClose(): void {
    this.requestedOpen.set(false);
    this.importResolved.set(false);
    this.importableApplications.set([]);
    this.modal()?.close();
  }
}
